import { archOf, spanTeeth, type ToothNumber } from './fdi';

/**
 * Bridges and titanium bars. A span is a named run of teeth from one tooth to
 * another along an arch; the chart draws a line above the teeth it covers.
 *
 * The rules here are the contract's rules, in executable form, so that the
 * mock server, the intake form and the tests share one definition and one
 * wording. The messages are the wording the server returns and the form
 * shows; change them here and nowhere else.
 */

export const SPAN_KINDS = ['bridge', 'titaniumBar'] as const;
export type SpanKind = (typeof SPAN_KINDS)[number];

export const SPAN_KIND_LABEL: Record<SpanKind, string> = {
  bridge: 'Bridge',
  titaniumBar: 'Titanium bar',
};

/** What the client sends. The server assigns the id. */
export interface NewSpan {
  name: string;
  kind: SpanKind;
  from: string;
  to: string;
}

export interface Span extends NewSpan {
  id: string;
}

export const SPAN_RULE = {
  oneArch: 'A bridge or bar stays on one arch',
  twoTeeth: 'A bridge or bar runs from one tooth to another',
  unmarked: (tooth: string) => `Mark tooth ${tooth} on the chart first`,
  sharedName: 'Two bridges or bars cannot share a name',
  overlap: {
    bridge: 'Two bridges cannot cover the same tooth',
    titaniumBar: 'Two bars cannot cover the same tooth',
  } satisfies Record<SpanKind, string>,
} as const;

/** The teeth a span covers, in arch order. The span must be on one arch. */
export function spanCovers(span: Pick<NewSpan, 'from' | 'to'>): ToothNumber[] {
  return spanTeeth(span.from as ToothNumber, span.to as ToothNumber);
}

/**
 * What is wrong with a span, as data rather than words, so a screen can say it
 * in its own strings and the server in the contract's. `spanProblemMessage`
 * turns one into the contract's wording.
 */
export type SpanProblem =
  | { rule: 'oneArch' }
  | { rule: 'twoTeeth' }
  | { rule: 'unmarked'; tooth: string }
  | { rule: 'sharedName' }
  | { rule: 'overlap'; kind: SpanKind };

export function spanProblemMessage(problem: SpanProblem): string {
  switch (problem.rule) {
    case 'oneArch':
      return SPAN_RULE.oneArch;
    case 'twoTeeth':
      return SPAN_RULE.twoTeeth;
    case 'unmarked':
      return SPAN_RULE.unmarked(problem.tooth);
    case 'sharedName':
      return SPAN_RULE.sharedName;
    case 'overlap':
      return SPAN_RULE.overlap[problem.kind];
  }
}

/**
 * The rules one span must satisfy on its own, in the order the contract lists
 * them. Returns the first that fails, or null when the span is sound.
 *
 * `teeth` is the case's chart: every covered tooth has to be on it, because a
 * bridge over a tooth nobody is making is a bridge over nothing.
 */
function problemOf(span: NewSpan, teeth: readonly string[]): SpanProblem | null {
  const from = span.from as ToothNumber;
  const to = span.to as ToothNumber;

  if (archOf(from) !== archOf(to)) return { rule: 'oneArch' };
  if (from === to) return { rule: 'twoTeeth' };

  const missing = spanTeeth(from, to).find((tooth) => !teeth.includes(tooth));
  return missing === undefined ? null : { rule: 'unmarked', tooth: missing };
}

/** The same, in the contract's wording. */
export function spanIsValid(span: NewSpan, teeth: readonly string[]): string | null {
  const problem = problemOf(span, teeth);
  return problem === null ? null : spanProblemMessage(problem);
}

/**
 * Two spans overlap when they are the same kind and share a tooth. A bar over
 * a bridge is allowed, which is why the kind is part of the test: the two draw
 * on different levels of the chart. Both spans must be on one arch already.
 */
export function spansOverlap(a: NewSpan, b: NewSpan): boolean {
  if (a.kind !== b.kind) return false;
  const covered = new Set<string>(spanCovers(a));
  return spanCovers(b).some((tooth) => covered.has(tooth));
}

/**
 * Every rule, over the whole list, giving one message per offending span in
 * the order sent and null for each sound one. A span that fails on its own is
 * not then compared with the others: the message it already carries is the
 * thing to fix first, and comparing a cross arch span would mean computing a
 * run that does not exist.
 */
export function spanProblems(
  spans: readonly NewSpan[],
  teeth: readonly string[],
): (SpanProblem | null)[] {
  const results: (SpanProblem | null)[] = spans.map((span) => problemOf(span, teeth));

  spans.forEach((span, index) => {
    if (results[index] !== null) return;

    const earlier = spans.slice(0, index);
    const name = span.name.trim().toLowerCase();
    if (earlier.some((other) => other.name.trim().toLowerCase() === name)) {
      results[index] = { rule: 'sharedName' };
      return;
    }

    const clashes = earlier.some(
      (other, otherIndex) => results[otherIndex] === null && spansOverlap(other, span),
    );
    if (clashes) results[index] = { rule: 'overlap', kind: span.kind };
  });

  return results;
}

/** The same, in the contract's wording: what the server returns and the tests read. */
export function validateSpans(
  spans: readonly NewSpan[],
  teeth: readonly string[],
): (string | null)[] {
  return spanProblems(spans, teeth).map((problem) =>
    problem === null ? null : spanProblemMessage(problem),
  );
}

/** True when nothing in the list breaks a rule. */
export function spansAreValid(spans: readonly NewSpan[], teeth: readonly string[]): boolean {
  return validateSpans(spans, teeth).every((message) => message === null);
}
